import React, { FC } from 'react';
import { queryClient } from '~/App';
import { TenantType } from '~/api/utils';
import { tenantQueryKey } from '~/hooks/tenant';
import { useTranslate } from 'react-admin';
import { SelectInput } from 'react-admin';
import { Business, Individual } from '~/types';
import { Configuration } from '~/api/solutions';
import { get } from 'lodash';
import palette from '~/theme/palette';
import { useHandbooksQuery } from '~/hooks/useHandbook';

type BranchSelectProps = {
  source: string;
  record?: Business | Individual | Configuration;
  className?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  [x: string]: any;
};

const label = (key: string): string => `components.ra.inputs.BranchSelect.${key}`;

const BranchSelect: FC<BranchSelectProps> = ({
  source,
  record,
  className,
  allowEmpty = true,
  disabled,
  ...rest
}) => {
  const translate = useTranslate();
  const t = (key: string, args?: any): string => translate(label(key), args);

  const tenant = queryClient.getQueryData<TenantType>(tenantQueryKey);

  const { data: handbook, isFetching: loadingHandbook } = useHandbooksQuery(tenant?.id);
  const handbookBranches =
    handbook?.branches
      ?.map((branch) => ({
        id: branch.id,
        name: branch.name || branch.id,
      }))
      ?.sort((a, b) => a.name.localeCompare(b.name)) || [];

  const sourceBranch = get(record, source);

  const extraChoice =
    sourceBranch && !handbookBranches.some((b) => b.id === sourceBranch)
      ? [{ id: sourceBranch, name: sourceBranch, isExtra: true }]
      : [];
  const choices = loadingHandbook ? [] : [...handbookBranches, ...extraChoice];

  const validateBranch = (value: any) => {
    if (value === null || value === undefined || value === '' || !choices?.length) return undefined;
    const exists = handbookBranches.some((branch) => branch.id === value);
    return exists ? undefined : t('invalidStoredValue');
  };
  return (
    <SelectInput
      label={t('branch')}
      source={source}
      choices={choices}
      allowEmpty={allowEmpty}
      disabled={disabled || loadingHandbook}
      className={className}
      validate={validateBranch}
      optionText={(choice: any) =>
        choice?.isExtra ? (
          <span style={{ color: palette.error.main }}>{choice?.name ?? choice?.id}</span>
        ) : (
          choice?.name ?? choice?.id
        )
      }
      {...rest}
    />
  );
};

export default BranchSelect;
